import React, { useMemo, useState } from "react";
import clxs from "./price-calculator-header.module.css";
import useClx from "../../../../../hooks/use-clx";
import LoyaltyPointShortTicker from "../../../../LoyaltyPointShortTicker";
import TaxTooltip from "../../../../Checkout/TaxTooltip";
import { useQueryParamValue } from "../../../../../hooks/use-query-param";
import { usePriceCalculatorValue } from "../../../../../hooks/use-price-calculator";
import { differenceInDays } from "date-fns";
import Button from "../../../../Button";

interface PriceCalculatorHeaderProps {
  className?: string;
  checkinDateKey: string;
  checkoutDateKey: string;
  couponCodeKey: string;
  id: string;
}

function PriceCalculatorHeader(props: PriceCalculatorHeaderProps) {
  const { className, checkinDateKey, checkoutDateKey, id, couponCodeKey } =
      props,
    ccx = useClx(clxs.pricingContainer, className),
    { tentativePrice } = usePriceCalculatorValue(id),
    [isDisableBooking, setIsDisableBooking] = useState<boolean>(false),
    checkinDate = useQueryParamValue("date", checkinDateKey),
    checkoutDate = useQueryParamValue("date", checkoutDateKey),
    couponCode = useQueryParamValue("string", couponCodeKey),
    currency = useMemo(
      () => tentativePrice?.currency_symbol || "",
      [tentativePrice],
    ),
    isUnavailable = useMemo(
      () => tentativePrice?.status === "unavailable",
      [tentativePrice],
    ),
    total = useMemo(() => {
      const total = tentativePrice?.breakdown?.config_total || 0;

      if (!total) {
        setIsDisableBooking(true);
        return "-";
      }
      setIsDisableBooking(false);
      return `${currency} ${total.toLocaleString("en-IN")}`;
    }, [tentativePrice]),
    average_base_rate = useMemo(() => {
      const average_base_rate = tentativePrice?.average_base_rate;

      if (average_base_rate === undefined) {
        return null;
      }

      const total = tentativePrice?.average_non_discounted_rate || 0;

      if (average_base_rate === total) {
        return null;
      }

      // const discount_method = tentativePrice?.breakdown.discount_method;
      // if(discount_method.toLowerCase() != couponCode.toLowerCase()) {
      //   const coupon_code = tentativePrice?.breakdown.coupon_code;
      //   setCouponCode(coupon_code ? coupon_code : "");
      // }

      return `${currency} ${average_base_rate.toLocaleString("en-IN")}`;
    }, [tentativePrice]),
    average_non_discounted_rate = useMemo(() => {
      const average_non_discounted_rate =
        tentativePrice?.average_non_discounted_rate || 0;

      if (!average_non_discounted_rate) {
        setIsDisableBooking(true);
        return "-";
      }
      setIsDisableBooking(false);
      return `${currency} ${average_non_discounted_rate.toLocaleString(
        "en-IN",
      )}`;
    }, [tentativePrice]),
    totalNights = useMemo(() => {
      if (!checkinDate || !checkoutDate) {
        return 0;
      }

      const diff = differenceInDays(checkoutDate, checkinDate);

      return diff;
    }, [checkinDate, checkoutDate]),
    discountValue = useMemo(() => {
      switch (tentativePrice.breakdown.discount_type) {
        case "free_night":
          return `(${tentativePrice.discount_value} Night)`;
        case "percentage_discount":
          return `(${tentativePrice.discount_value}%)`;
        case "flat_discount":
          return `(${
            tentativePrice.currency_symbol
          }${tentativePrice.discount_value.toLocaleString("en-IN")})`;
        default:
          return "";
      }
    }, [tentativePrice]),
    actionText = useMemo(() => {
      if (isUnavailable) {
        return "Enquire";
      }

      if (tentativePrice?.sold_out) {
        return "Enquire";
      }

      if (!checkinDate || !checkoutDate) {
        return "Select dates";
      }

      return "Book";
    }, [tentativePrice]),
    taxTemplate = useMemo(() => {
      if (!tentativePrice) {
        return [];
      }
      const template: TaxBreakdown[] = [
        {
          taxes: tentativePrice.breakdown?.gross_base_rate || 0,
          sub_title: `${average_non_discounted_rate || average_base_rate}  x ${
            totalNights ? totalNights : "1"
          }`,
          description: "Stay charges",
        },
        {
          taxes: tentativePrice.tier_discount
            ? tentativePrice.tier_discount
            : tentativePrice.discount,
          sub_title: `${couponCode === "NONE" ? "" : couponCode} ${
            tentativePrice.tier_discount_percentage > 0
              ? `(${tentativePrice.tier_discount_percentage}%)`
              : tentativePrice.discount_value > 0
              ? discountValue
              : ""
          }`,
          description: "Discount",
        },
        {
          taxes: tentativePrice.breakdown?.config_taxes || 0,
          sub_title: `${tentativePrice.tax_percentage}%`,
          description: tentativePrice.currency_symbol === "₹" ? "GST" : "tax",
        },
        {
          taxes: tentativePrice.breakdown?.config_total || 0,
          sub_title: "",
          description: "Total Payable",
        },
      ];

      return template as TaxBreakdown[];
    }, [tentativePrice]);

  const handleSubmitClick = () => {
    const event = new Event("headerBookClicked");
    window.dispatchEvent(event);
  };

  return (
    <div className={ccx}>
      <div className={clxs.discountedContainer}>
        <span className={clxs.startsFrom}>
          {!checkinDate || !checkoutDate ? "starts from" : ""}
        </span>
        {average_base_rate && (
          <span className={clxs.strike}>{average_non_discounted_rate}</span>
        )}
        {average_base_rate && (
          <div className={clxs.excTax}>
            <LoyaltyPointShortTicker
              discountType={tentativePrice?.breakdown.discount_type}
              method={tentativePrice?.breakdown.discount_method}
              discountPercentage={
                tentativePrice.discount_value
                  ? tentativePrice.discount_value
                  : tentativePrice?.breakdown.loyalty_points_discount_percentage
              }
              className={clxs.llpShortTicker}
              currencySymbol={tentativePrice.currency_symbol}
            />
          </div>
        )}
        <div className={clxs.actualPricingContainer}>
          <span className={clxs.rate}>
            {average_base_rate || average_non_discounted_rate}
          </span>
          <div className={clxs.subscript}>Per night excluding taxes*</div>
        </div>
      </div>

      <div className={clxs.total}>
        Total: {total}
        <TaxTooltip
          tax_data={taxTemplate}
          currency_symbol={tentativePrice.currency_symbol}
          hideSymbole={true}
          title="Total Summary"
        />
      </div>

      <div className={clxs.bookContainer}>
        <Button
          className={clxs.book}
          type="submit"
          disabled={isDisableBooking}
          onClick={handleSubmitClick}
        >
          {actionText}
        </Button>
      </div>
    </div>
  );
}

export default PriceCalculatorHeader;
